import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useRealtimeNotificationListener } from '../../modules/RealtimeNotificationsProvider';
import { ROUTES_PATH } from '../../constants/Routes';
import { eventTracker } from 'app/modules/Segment';
import {
  FeatureEnum,
  GroupIdEnum,
  ModuleEnum,
  OwnerEnum,
} from 'app/modules/Segment/types';
import * as S from './RealtimeToastPoc.styles';

const TOAST_AUTO_CLOSE_MS = 10000;
const TOAST_NOTIFICATION_TYPE = 'jusprocessos_new_process_found';
const MEUS_PROCESSOS_PATH = `${ROUTES_PATH.LAWSUIT_MONITOR.BASE_V2}${ROUTES_PATH.LAWSUIT_MONITOR.LAWSUITS}`;

const TOAST_EVENT_CONTEXT = {
  groupId: GroupIdEnum.JUSPROCESSOS_GROUP,
  library: {
    name: 'jusprocessos-library',
    componente: 'realtime-toast',
  },
};

const renderMainText = (text) => {
  const match = text.match(/^(.*?\bOAB\s+)([A-Z]{2}\d+)(.*)$/);

  if (!match) return text;

  return (
    <>
      {match[1]}
      <strong>{match[2]}</strong>
      {match[3]}
    </>
  );
};

// Evita renderizar JSON cru no toast (ex.: mensagens vindas do caminho legado
// do notification-service, onde `message` é o envelope SNS inteiro).
const isProbablyJson = (value) =>
  typeof value === 'string' && /^\s*[{[]/.test(value.trim());

const renderMessage = (text) => {
  const ctaMatch = text.match(/^(.*?)\s+(Clique aqui para ver[\s\S]*)$/i);

  if (!ctaMatch) return renderMainText(text);

  return (
    <>
      <span>{renderMainText(ctaMatch[1].trim())}</span>
      <S.Cta>{ctaMatch[2].trim()}</S.Cta>
    </>
  );
};

const RealtimeToastPoc = () => {
  const { id: userId, client_id: clientId } =
    useSelector((state) => state.auth?.user) || {};
  const queueRef = useRef([]);
  const activeToastIdRef = useRef(null);

  useEffect(() => {
    if (clientId !== undefined && clientId !== null) {
      console.log('[RealtimeToastPoc] socket bound to clientId =', clientId);
    }
  }, [clientId]);

  const trackToastDisplayed = useCallback(
    (notification) => {
      eventTracker.track({
        event: 'jus_processos_realtime_toast_displayed',
        properties: {
          owner: OwnerEnum.GDC,
          feature: FeatureEnum.JUS_PROCESSOS,
          module: ModuleEnum.JUS_PROCESSOS,
          event_text: 'JusProcessos Realtime Toast Displayed',
          relevant_user_activity: false,
          client_id: clientId,
          user_id: userId,
          title: notification?.title,
          event_date: new Date().toISOString(),
          event_meaning:
            'Disparado quando o toast de notificação em tempo real de novo processo é exibido para o usuário',
        },
        context: TOAST_EVENT_CONTEXT,
      });
    },
    [clientId, userId],
  );

  const trackToastClicked = useCallback(
    (notification) => {
      eventTracker.track({
        event: 'jus_processos_realtime_toast_clicked',
        properties: {
          owner: OwnerEnum.GDC,
          feature: FeatureEnum.JUS_PROCESSOS,
          module: ModuleEnum.JUS_PROCESSOS,
          event_text: 'JusProcessos Realtime Toast Clicked',
          relevant_user_activity: true,
          client_id: clientId,
          user_id: userId,
          message: notification?.message,
          event_date: new Date().toISOString(),
          event_meaning:
            'Disparado quando o usuário clica no toast de notificação em tempo real para abrir a página de meus processos',
        },
        context: TOAST_EVENT_CONTEXT,
      });
    },
    [clientId, userId],
  );

  const showNextToast = useCallback(() => {
    if (activeToastIdRef.current !== null) return;

    const next = queueRef.current.shift();

    if (!next) return;

    const rawMessage = next?.message;
    const message =
      rawMessage && !isProbablyJson(rawMessage)
        ? rawMessage
        : next?.title || 'Nova notificação';

    const id = toast(<S.Content>{renderMessage(message)}</S.Content>, {
      autoClose: TOAST_AUTO_CLOSE_MS,
      onClick: () => {
        trackToastClicked(next);
        window.open(MEUS_PROCESSOS_PATH, '_blank', 'noopener,noreferrer');
      },
      onClose: () => {
        activeToastIdRef.current = null;
        showNextToast();
      },
      closeOnClick: true,
      pauseOnHover: true,
      hideProgressBar: false,
      draggable: false,
      className: 'realtime-toast',
      bodyClassName: 'realtime-toast__body',
      progressClassName: 'realtime-toast__progress',
    });

    activeToastIdRef.current = id;
    trackToastDisplayed(next);
  }, [trackToastClicked, trackToastDisplayed]);

  useRealtimeNotificationListener((notification) => {
    if (notification?.type !== TOAST_NOTIFICATION_TYPE) return;

    queueRef.current.push(notification);
    showNextToast();
    console.log('[RealtimeToastPoc] received notification', notification);
  });

  return <S.RealtimeToastStyles />;
};

export default RealtimeToastPoc;
